import {
  Button,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  openConfirmDialog,
} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {ReactNode} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {
  TelephonyInboundRecordingNotice,
  TelephonyOutboundRecordingNotice,
  TelephonyRecordingSettings,
  useUpdateTelephonyRecordingSettingsMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  getTelephonyRecordingSettingsFormValues,
  getUpdateTelephonyRecordingSettingsBody,
  RECORDING_NOTICE_MAX_LENGTH,
  telephonyRecordingSettingsFormSchema,
  TelephonyRecordingSettingsFormValues,
} from '../utils/telephonyRecordingSettingsFormSchema';

const RETENTION_INPUT_MAX_WIDTH = 100;
const NOTICE_TEXT_MIN_ROWS = 2;

/** The automatic notice of each direction, which Cancel in the confirmation puts back. */
const AUTOMATIC_INBOUND_NOTICE: TelephonyInboundRecordingNotice = 'before_ringing';
const AUTOMATIC_OUTBOUND_NOTICE: TelephonyOutboundRecordingNotice = 'on_answer';

interface TelephonyRecordingSettingsFormProps extends RequiredTestIdProps {
  settings: TelephonyRecordingSettings;
  /** The tenant language the notice is spoken in, by name; null when unknown. */
  languageName: string | null;
  /** The warning and the AI-assistant line, the same as in the read view. */
  notes: (values: NoticeValues) => ReactNode;
  /** After a save: re-reads the settings (the PUT returns nothing) and leaves the form. */
  onSaved: () => Promise<unknown>;
  onDiscard: VoidFunction;
}

/**
 * Picking a "says it" option asks for the confirmation first; Cancel puts the automatic notice
 * back. The save then tells the backend the new `operator` direction was confirmed.
 */
export function TelephonyRecordingSettingsForm(props: TelephonyRecordingSettingsFormProps) {
  const [updateSettings] = useUpdateTelephonyRecordingSettingsMutation();

  const handleSubmit: FormSubmitHandler<TelephonyRecordingSettingsFormValues> = (values) =>
    updateSettings({body: getUpdateTelephonyRecordingSettingsBody(values, props.settings)})
      .unwrap()
      .then(props.onSaved)
      .catch(handleApiError);

  const noticeTextHelper = [
    isNil(props.languageName)
      ? null
      : i18n.t('page.settings.telephony.recordingNoticeSpokenIn', {language: props.languageName}),
    i18n.t('page.settings.telephony.recordingNoticeTextHelper'),
    i18n.t('page.settings.telephony.spokenTextLengthHint'),
  ]
    .filter(isNotNil)
    .join(' ');

  return (
    <Form<TelephonyRecordingSettingsFormValues>
      defaultValues={getTelephonyRecordingSettingsFormValues(props.settings)}
      experimentalZodSchema={telephonyRecordingSettingsFormSchema}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => {
        const confirmOperator = (revert: VoidFunction) => {
          let isConfirmed = false;

          openConfirmDialog({
            id: 'confirmOperatorRecordingNotice',
            text: i18n.t('page.settings.telephony.recordingOperatorConfirm'),
            onConfirm: () => {
              isConfirmed = true;
            },
            onCloseComplete: () => {
              if (!isConfirmed) {
                revert();
              }
            },
            'data-testid': suffixTestId('confirmOperator', props),
          });
        };

        const handleInboundChange = (value: string | null) => {
          if (value === 'operator' && props.settings.noticeInbound !== 'operator') {
            confirmOperator(() => formApi.setValue('noticeInbound', AUTOMATIC_INBOUND_NOTICE));
          }
        };

        const handleOutboundChange = (value: string | null) => {
          if (value === 'operator' && props.settings.noticeOutbound !== 'operator') {
            confirmOperator(() => formApi.setValue('noticeOutbound', AUTOMATIC_OUTBOUND_NOTICE));
          }
        };

        return (
          <VStack spacing={4}>
            <Box maxWidth={RETENTION_INPUT_MAX_WIDTH}>
              <FormField
                control={control}
                type="integer"
                name="retentionDays"
                label={i18n.t('page.settings.telephony.recordingRetention')}
                suffix={i18n.t('page.settings.telephony.recordingRetentionUnit')}
                helperText={i18n.t('page.settings.telephony.recordingRetentionHelper')}
                isRequired
                data-testid={suffixTestId('retentionDays', props)}
              />
            </Box>
            <FormField
              control={control}
              type="radio"
              name="noticeInbound"
              label={i18n.t('page.settings.telephony.recordingNoticeInbound')}
              direction="column"
              spacing={2}
              options={[
                {
                  value: AUTOMATIC_INBOUND_NOTICE,
                  label: i18n.t('page.settings.telephony.recordingNoticeBeforeRinging'),
                },
                {
                  value: 'operator',
                  label: i18n.t('page.settings.telephony.recordingNoticeInboundOperator'),
                },
              ]}
              onChange={handleInboundChange}
              data-testid={suffixTestId('noticeInbound', props)}
            />
            <FormField
              control={control}
              type="radio"
              name="noticeOutbound"
              label={i18n.t('page.settings.telephony.recordingNoticeOutbound')}
              direction="column"
              spacing={2}
              options={[
                {
                  value: AUTOMATIC_OUTBOUND_NOTICE,
                  label: i18n.t('page.settings.telephony.recordingNoticeOnAnswer'),
                },
                {
                  value: 'operator',
                  label: i18n.t('page.settings.telephony.recordingNoticeOutboundOperator'),
                },
              ]}
              onChange={handleOutboundChange}
              data-testid={suffixTestId('noticeOutbound', props)}
            />
            <FormField
              control={control}
              type="textarea"
              name="noticeText"
              label={i18n.t('page.settings.telephony.recordingNoticeText')}
              placeholder={props.settings.defaultNoticeText}
              minRows={NOTICE_TEXT_MIN_ROWS}
              maxLength={RECORDING_NOTICE_MAX_LENGTH}
              isCounterVisible
              helperText={noticeTextHelper}
              data-testid={suffixTestId('noticeText', props)}
            />
            <NotesForValues control={control} notes={props.notes} />
            <HStack spacing={2} justify="flex-end">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discardChanges')}
                onClick={props.onDiscard}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.saveChanges')}
                data-testid={suffixTestId('save', props)}
              />
            </HStack>
          </VStack>
        );
      }}
    </Form>
  );
}

type NoticeValues = Pick<TelephonyRecordingSettingsFormValues, 'noticeInbound' | 'noticeOutbound'>;

interface NotesForValuesProps {
  control: FormControl<TelephonyRecordingSettingsFormValues>;
  notes: (values: NoticeValues) => ReactNode;
}

/** The notes follow the picked options as they change, before the save. */
function NotesForValues(props: NotesForValuesProps) {
  const [noticeInbound, noticeOutbound] = useWatch({
    control: props.control,
    name: ['noticeInbound', 'noticeOutbound'],
  });

  return props.notes({noticeInbound, noticeOutbound});
}
